import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import { Component, inject, Input, signal, ViewChild } from '@angular/core';

import { trackByProp } from '@recruitee/common';
import { injectTranslate } from '@recruitee/i18n';

import { IconButtonComponent } from '../../../../../common/button/components/icon-button/icon-button.component';
import { IconComponent } from '../../../../../common/icon/icon.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../../../../core/overlay/overlay-directives';
import {
  ObservedEntry,
  ResizeObserverEntryDirective,
} from '../../../../../resize-observer/resize-observer-entry.directive';
import { Select2TriggerDirective } from '../../../default-templates/trigger/trigger.directive';
import { Select2Item } from '../../../types';
import { MULTISELECT_FEATURE } from '../types';

@Component({
  selector: 'rt-select-trigger-multi',
  standalone: true,
  imports: [
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    NgTemplateOutlet,
    ResizeObserverEntryDirective,
    IconButtonComponent,
    IconComponent,
    Select2TriggerDirective,
    JsonPipe,
  ],
  templateUrl: './trigger-default.component.html',
  styleUrls: ['./trigger-default.component.less'],
  providers: [
    {
      provide: Select2TriggerDirective,
      useFactory: () => inject(MultiSelectTriggerComponent).directive,
    },
  ],
})
export class MultiSelectTriggerComponent {
  @ViewChild(Select2TriggerDirective, { static: true })
  public directive: Select2TriggerDirective<null>;
  @Input() public placeholder = signal<string>(injectTranslate()('rtu.ui.select.placeholder'));

  public trackByValue = trackByProp<Select2Item>({ prop: 'value' });
  public visibleCount: number = Number.MAX_SAFE_INTEGER;
  public hiddenItemIndex: number | null = null;
  public moreOffset: number;

  public multiselectFeature = inject(MULTISELECT_FEATURE);
  public itemSelector = this.multiselectFeature.selectController.itemSelector;

  public onResized(
    event: ObservedEntry,
    multiselectContainer: HTMLElement,
    selectionContainer: HTMLElement,
    moreIndicator: HTMLElement,
  ) {
    const { detectChanges$ } = this.multiselectFeature.selectController;
    const indicatorsWidth = 63;
    const containerHeight = event.height;
    const multiselectRect = multiselectContainer.getBoundingClientRect();
    if (containerHeight > multiselectRect.height) {
      const children = Array.from(selectionContainer.children);
      const outsideIndex = children.findIndex(el => {
        const childRect = el.getBoundingClientRect();
        return childRect.bottom > multiselectRect.bottom;
      });
      const lastInRowIndex = outsideIndex - 1;
      const lastInRow = <HTMLElement>children[lastInRowIndex];
      this.visibleCount = outsideIndex;
      detectChanges$.next();
      const maxOffset = multiselectRect.width - indicatorsWidth - moreIndicator.offsetWidth;
      this.moreOffset = lastInRow.offsetLeft + lastInRow.offsetWidth + 4;
      if (this.moreOffset > maxOffset) {
        this.hiddenItemIndex = lastInRowIndex;
        this.visibleCount = this.visibleCount - 1;
        this.moreOffset = lastInRow.offsetLeft - lastInRowIndex + 2;
      } else {
        this.hiddenItemIndex = null;
      }
    } else {
      this.visibleCount = Number.MAX_SAFE_INTEGER;
      this.hiddenItemIndex = null;
    }
    detectChanges$.next();
  }

  public remove = (item: Select2Item) => {
    const { selectController, chipRemoved } = this.multiselectFeature;
    selectController.itemSelector.remove(item);
    chipRemoved.emit();
  };
}
